import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { AppConfig } from '../../../core-bundle/config/app-config';
import Icon from '@wetransfer/transfer/component/icon';
import ItemList from '@wetransfer/transfer/component/itemlist';
import Scrollable from '@wetransfer/transfer/component/scrollable';
import TransferLink from '@wetransfer/transfer/component/transfer-link';
import GridButton from '@wetransfer/transfer/component/transfer/downloader/grid-button';
import ExperimentWorkspaceTrackingImplementation from '@wetransfer/transfer/constant/experiments/workspace-tracking-implementation';
import NotificationPositions from '@wetransfer/transfer/constant/notification-positions';
import TransferLinkSources from '@wetransfer/transfer/constant/transfer-link-sources';
import { getTransferPriceAsFloat } from '@wetransfer/transfer/helper/paidTransfers';
import PreviewHelper from '@wetransfer/transfer/helper/preview';
import {
  getTotalFileSize,
  getTransferFileById,
  getTransferNumberOfFiles,
} from '@wetransfer/transfer/helper/transferFiles';
import { getTransferType } from '@wetransfer/transfer/helper/transferType';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import {
  getDownloadFullEvent,
  getDownloadPartialEvent,
} from '@wetransfer/transfer/modules/analytics/vendors/utils/trackDownload';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import I18n from '@wetransfer/transfer/modules/i18n';
import Router from '@wetransfer/transfer/modules/router';
import Grid from '@wetransfer/transfer/page/transfer/grid';
import Store from '@wetransfer/transfer/store';
import Actions from '@wetransfer/transfer/store/action';
import { selectTransferAmplitudeEventWallpaperProperties } from '@wetransfer/transfer/store/selector/analytics';
import { canBeShownAsGrid } from '@wetransfer/transfer/transfer-bundle/helpers/preview';
import { TransferDownloadActions } from '@wetransfer/transfer/transfer-bundle/store/download-actions';

import { DisplayName } from './DisplayName';
import { Message } from './Message';
import {
  MobileWebActions,
  MobileWebContainer,
  MobileWebDescription,
} from './MobileWebContainer';

export default class Files extends React.Component {
  state = {
    isReady: false,
    textExpanded: false,
    showAllFilesOnMobile: false,
    transferLinkButtonTimerId: null,
    linkCopied: false,
    showTransferLink: false,
  };

  constructor(props) {
    super(props);

    this.panelContainer = undefined;
    this.onDownloadAll = this.onDownloadAll.bind(this);
    this.onExpandText = this.onExpandText.bind(this);
    this.onFileDownload = this.onFileDownload.bind(this);
    this.onShowAllFiles = this.onShowAllFiles.bind(this);
    this.trackOpenInAppButton = this.trackOpenInAppButton.bind(this);
    this.trackDownloadAppButton = this.trackDownloadAppButton.bind(this);
    this.shareNative = this.shareNative.bind(this);
  }

  componentDidMount() {
    const { route, session } = this.props;
    const onGrid = route.path.includes('/grid');

    this.setState({ showTransferLink: !navigator.canShare });

    if (onGrid || !this.previewable) {
      return;
    }

    if (Browser.isMobile) {
      this.mobileGridTimeout = setTimeout(
        () =>
          Router.navigateTo(
            PreviewHelper.getGridUrl({
              transferId: this.props.transfer.id,
              recipientId: this.props.recipient,
              secret: this.props.secret,
            })
          ),
        0
      ); // Async
    } else if (!Cookie.get('wt_discover_vrl') && session && session.tandc) {
      Cookie.set('wt_discover_vrl', 1, { days: 365 });
      this.pushTransferNotification = setTimeout(() => {
        Store.dispatch(
          Actions.Notification.pushTransfer({
            text: 'DiscoverVrlTooltip',
            align: '.downloader__grid-button',
          })
        );
      }, 1000);
    }

    if (!this.props.file_id) return;

    this.scrollToActiveFile();
  }

  componentWillUnmount() {
    const { route } = this.props;

    if (Browser.isMobile && route.path.includes('/grid')) {
      const downloadUrl = route.path.split('/grid')[0];
      Router.navigateTo(downloadUrl);
    }

    clearTimeout(this.mobileGridTimeout);
    clearTimeout(this.pushTransferNotification);
  }

  get previewable() {
    return canBeShownAsGrid(this.props.transfer.itemsTree.items);
  }

  /**
   * If the file_id changes, call the scrollToActiveFile method
   *
   * @param {object} prevProps  Components' previous props
   */
  componentDidUpdate(prevProps) {
    if (prevProps.file_id !== this.props.file_id) {
      this.scrollToActiveFile();
    }
  }

  /**
   * If there is a mounted scrollable component, scroll to the active file.
   */
  scrollToActiveFile() {
    if (!this.node) {
      return;
    }

    const previewElem = this.node.querySelector('.filelist__item--previewing');

    if (!previewElem) {
      return;
    }

    if (this.scrollable) {
      if (this.scrollable.isNodeInView(previewElem)) {
        return;
      }

      return this.scrollable.scrollTo(previewElem, {
        align: 'bottom',
        animated: true,
        duration: 250,
      });
    }

    return previewElem.scrollIntoView();
  }

  // We want to download a transfer or single file, so request from the API
  // where we can obtain the file and then go get it!
  async onDownloadAll() {
    const { transfer, session, currentSize } = this.props;

    this.trackDownloadEvent({
      action: 'click_download_all',
      label: 'transfer_window',
    });

    const {
      wallpaperNumber,
      wallpaperType,
      wallpaperFileType,
      wallpaperSource,
    } = selectTransferAmplitudeEventWallpaperProperties(Store.getState());

    const transferType = getTransferType(transfer);
    const transferPrice = getTransferPriceAsFloat(transfer);
    const transferSize = getTotalFileSize(transfer);
    const numberOfFiles = getTransferNumberOfFiles(transfer);

    trackAmplitudeEvent(
      getDownloadFullEvent(
        transferType,
        numberOfFiles,
        transfer.id,
        ExperimentWorkspaceTrackingImplementation.isWorkspaceTracking()
          ? transferSize
          : undefined,
        wallpaperNumber,
        wallpaperType,
        wallpaperFileType,
        wallpaperSource,
        transferPrice
      )
    );

    await Store.dispatch(
      TransferDownloadActions.downloadTransfer({
        transfer,
        secret: this.props.secret,
        recipientId: this.props.recipient || false,
        domainUserId: session.domainUserId,
        currentSize,
      })
    );
  }

  trackDownloadEvent = ({ action, label }) => {
    Analytics.trackSnowplowEvent({
      category: 'download',
      action,
      label,
    });
  };

  onExpandText() {
    this.setState({ textExpanded: true });
  }

  onShowAllFiles() {
    this.setState({ showAllFilesOnMobile: true });
  }

  async onFileDownload(id) {
    const { transfer, session } = this.props;

    this.trackDownloadEvent({
      action: 'click_download_file',
      label: 'transfer_window',
    });

    const {
      wallpaperNumber,
      wallpaperType,
      wallpaperFileType,
      wallpaperSource,
    } = selectTransferAmplitudeEventWallpaperProperties(Store.getState());

    const transferType = getTransferType(transfer);
    const transferPrice = getTransferPriceAsFloat(transfer);
    const fileSize = getTransferFileById(transfer, id)?.size;

    trackAmplitudeEvent(
      getDownloadPartialEvent(
        transferType,
        transfer.id,
        ExperimentWorkspaceTrackingImplementation.isWorkspaceTracking()
          ? fileSize
          : undefined,
        wallpaperNumber,
        wallpaperType,
        wallpaperFileType,
        wallpaperSource,
        transferPrice
      )
    );

    await Store.dispatch(
      TransferDownloadActions.downloadTransferFile({
        transfer,
        secret: this.props.secret,
        recipientId: this.props.recipient || false,
        fileId: id,
        domainUserId: session.domainUserId,
      })
    );
  }

  renderDownloadIcon(transfer) {
    if (transfer.itemsTree.items.length > 2 && !this.previewable) {
      return null;
    }

    return (
      <Icon
        type={
          transfer.state === 'processing'
            ? 'TransferProcessing'
            : 'TransferReady'
        }
        className="downloader__top-icon"
      />
    );
  }

  trackOpenInAppButton = () => {
    Analytics.trackGoogleEvent({
      category: 'popup experiment',
      action: 'open in app button',
    });

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_open_in_app',
      property: 'button_download_page',
    });
  };

  trackDownloadAppButton = () => {
    Analytics.trackGoogleEvent({
      category: 'popup experiment',
      action: 'download app button',
    });

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_download_the_app',
      property: 'button_download_page',
    });
  };

  onTransferLinkCopied = () => {
    const { transferLinkButtonTimerId } = this.state;

    if (transferLinkButtonTimerId) {
      clearTimeout(transferLinkButtonTimerId);
    }

    const timerId = setTimeout(() => {
      this.setState({
        transferLinkButtonTimerId: null,
        linkCopied: true,
      });
    }, 4000);

    this.setState({
      transferLinkButtonTimerId: timerId,
    });
  };

  getTransferLinkLabel = () => {
    const { transferLinkButtonTimerId, linkCopied } = this.state;
    let transferLinkButtonLabel = I18n.t('transfer_link.buttons.copy');

    if (transferLinkButtonTimerId === null && linkCopied) {
      transferLinkButtonLabel = I18n.t('transfer_link.buttons.copy_again');
    }

    if (transferLinkButtonTimerId) {
      transferLinkButtonLabel = I18n.t('transfer_link.buttons.copied');
    }

    return transferLinkButtonLabel;
  };

  async shareNative() {
    Analytics.trackSnowplowEvent({
      category: 'receive',
      action: 'click_share_link',
      label: 'share_link_to_native',
    });

    try {
      await navigator.share({
        url: this.props.transfer.shortURL,
      });
    } catch {
      // Do nothing
    }
  }

  // Render our view
  render() {
    const { transfer, route, currentSize } = this.props;
    const totalItemsCount = transfer.items.length;
    const topLevelItemsCount = transfer.itemsTree.items.length;
    const buttonClassNames = ['transfer__button'];
    const isGrid = this.previewable;

    const hasFiles = transfer.items.some(
      ({ content_identifier }) => content_identifier === 'file'
    );
    let isActiveButton = true;
    let downloadLabel = 'download';
    const transferLinkButtonLabel = this.getTransferLinkLabel();

    if (transfer.currentFileDownload >= 0 || !this.props.isReady || !hasFiles) {
      isActiveButton = false;
      buttonClassNames.push('transfer__button--inactive');
    }

    // How long before the transfer expires?
    let description = I18n.t('transfer.download.expires', {
      relative_time: I18n.relativeTime(transfer.expiryInSeconds),
    });

    if (typeof transfer.expiryInSeconds === 'undefined') {
      description = I18n.t('transfer.download.expires_never');
    }

    const headingClassNames = ['downloader__heading'];

    if (!this.previewable) {
      headingClassNames.push(
        `downloader__heading--${topLevelItemsCount}-files`
      );
    } else {
      headingClassNames.push('downloader__heading--grid-available');
    }

    let message = null;

    const transferDisplayName = (
      <DisplayName
        hasMessage={Boolean(transfer.message)}
        content={transfer.displayName}
      />
    );

    if (transfer.message) {
      headingClassNames.push('downloader__heading--message');
      message = <Message content={transfer.message} />;
    }

    const title = this.props.isReady
      ? I18n.t('transfer.download.title')
      : I18n.t('transfer.pending.title');

    let itemList = null;

    const initialItemsNumber =
      AppConfig.layout.lists.initialItemsNumber.downloader;

    if (isGrid) {
      itemList = (
        <GridButton
          isPanelOpen={
            route.name === 'grid-panel' || route.name === 'preview-panel'
          }
          ready={this.props.isReady}
          transfer={transfer}
          currentSize={currentSize}
        />
      );
    } else {
      itemList = (
        <ItemList
          transfer={transfer}
          altText={I18n.t('transfer.pending.subtitle')}
          showPending={!this.props.isReady}
          maxFiles={
            !Browser.isMobile || this.state.showAllFilesOnMobile
              ? 0
              : initialItemsNumber
          }
          previewable={
            transfer.items.find((item) => item.previewable === true) !==
            undefined
          }
          onDownload={this.onFileDownload}
          id={this.props.id}
          secret={this.props.secret}
          recipient={this.props.recipient}
          file_id={this.props.file_id}
        />
      );
    }

    let content = (
      <Scrollable
        ref={(node) => (this.scrollable = node)}
        className="transfer__contents"
      >
        <div className={headingClassNames.join(' ')}>
          {this.renderDownloadIcon(transfer)}
          <h2>{title}</h2>
          <p>{description}</p>
          {transferDisplayName}
          {message}
        </div>
        {itemList}
      </Scrollable>
    );

    if (Browser.isMobile) {
      if (totalItemsCount > 1) {
        downloadLabel = 'download_all_files';
      }

      headingClassNames.push('downloader__heading--receiver-UI');

      let expiration = I18n.t('transfer.download.expires_in', {
        expiry: I18n.relativeTime(transfer.expiryInSeconds),
      });

      if (typeof transfer.expiryInSeconds === 'undefined') {
        expiration = I18n.t('transfer.download.expires_never');
      }

      content = [
        <MobileWebContainer
          key="render_download"
          className={headingClassNames.join(' ')}
          displayName={transferDisplayName}
          description={
            <MobileWebDescription
              totalItemsCount={totalItemsCount}
              currentSize={currentSize}
              expiration={expiration}
            />
          }
          actions={
            <MobileWebActions
              showSecondaryButton={
                !this.state.showTransferLink && transfer.shortURL
              }
              onPrimaryButtonClick={this.onDownloadAll}
              onSecondaryButtonClick={this.shareNative}
            />
          }
          message={message}
        />,
      ];

      if (isGrid) {
        content.push(
          <h3
            key="downloader__file-count-title"
            className="downloader__file-count-title"
          >
            {I18n.t('transfer.download.files_label')}
          </h3>
        );

        content.push(<Grid key="grid" route={route} />);
      } else {
        content.push(
          <h3
            key="downloader__file-count-title"
            className="downloader__file-count-title"
          >
            {I18n.t('transfer.download.files_nosize', {
              count: totalItemsCount,
            })}
          </h3>
        );
        content.push(itemList);

        if (
          !this.state.showAllFilesOnMobile &&
          topLevelItemsCount > initialItemsNumber
        ) {
          content.push(
            <span
              key="downloader__show-all-files"
              className="downloader__show-all-files"
              onClick={this.onShowAllFiles}
            >
              {I18n.t('transfer.download.show_all_files_mobile')}
            </span>
          );
        }
      }

      const downloadButtonLabel =
        totalItemsCount === 1 ? 'download_file' : 'download_all';

      buttonClassNames.push('downloader__button-with-icon');

      content.push(
        <button
          style={{ width: '100%' }}
          key="transfer_footer_action"
          tabIndex={this.props.acceptedTandC ? null : '-1'}
          onClick={this.onDownloadAll}
          className={buttonClassNames.join(' ')}
          disabled={!isActiveButton}
        >
          <Icon type="DownloadArrowUnderscored" />
          {I18n.t(`transfer.buttons.${downloadButtonLabel}`)}
        </button>
      );
    }

    const footerContents = [];

    if (!Browser.isMobile) {
      let tabIndex;

      const { isConsentFlagActive, isConsentStringValid, acceptedTandC } =
        this.props;

      // If the user is part of the cookiewall rollout and does not have a valid
      // consent string, this means the user has not interacted with the consent
      // wall and thus we shouldn't let them reach the input button.
      //
      // At this point, the user has already accepted the T and C.
      if (isConsentFlagActive && !isConsentStringValid) {
        tabIndex = '-1';
      }

      // If the user is not part of the cookiewall rollout and has not accepted the T and C,
      // then we shouldn't let the user reach the input button either.
      if (!isConsentFlagActive && !acceptedTandC) {
        tabIndex = '-1';
      }

      footerContents.push(
        <button
          key="transfer_footer_action"
          tabIndex={tabIndex}
          onClick={this.onDownloadAll}
          className={buttonClassNames.join(' ')}
          disabled={!isActiveButton}
        >
          {I18n.t(`transfer.buttons.${downloadLabel}`)}
        </button>
      );
    }

    if (Browser.isMobile && totalItemsCount > 1) {
      footerContents.push(
        <p key="zip_information">
          {I18n.t('transfer.download.download_all_zip')}
          <br />
          <a
            href="https://help.wetransfer.com/hc/en-us/articles/206816323-Why-do-you-zip-my-files-"
            rel="external"
          >
            {I18n.t('transfer.download.learn_more')}
          </a>
        </p>
      );

      if (transfer.shortURL && this.state.showTransferLink) {
        footerContents.push(
          <TransferLink
            key="transfer-link"
            buttonLabel={transferLinkButtonLabel}
            notificationPosition={NotificationPositions.none}
            className="downloader__copy-link"
            queryParams={{ src: TransferLinkSources.downloads }}
            onLinkCopied={this.onTransferLinkCopied}
          />
        );
      }
    }

    const classNames = ['transfer__window', 'downloader'];

    const footerClassNames = classnames('transfer__footer', {
      'transfer__footer--receiver-UI': Browser.isMobile,
    });

    // TODO: Info icon based on X hours remaining
    return (
      <React.Fragment>
        <div
          ref={(node) => (this.node = node)}
          className={classNames.join(' ')}
        >
          {content}
          <div className={footerClassNames}>{footerContents}</div>
        </div>
      </React.Fragment>
    );
  }
}

Files.propTypes = {
  transfer: PropTypes.object,
  id: PropTypes.string,
  secret: PropTypes.string,
  recipient: PropTypes.any,
  file_id: PropTypes.string,
  isReady: PropTypes.bool,
  route: PropTypes.object.isRequired,
  acceptedTandC: PropTypes.bool,
  session: PropTypes.object.isRequired,
  currentSize: PropTypes.number,
  isConsentFlagActive: PropTypes.bool,
  isConsentStringValid: PropTypes.bool,
};
